import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from './AuthContext';
import {
    DataGrid,
    GridToolbar
} from "@mui/x-data-grid";
import { ThemeProvider } from "@mui/material/styles";
import {
    Button,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Box,
    LinearProgress,
    Typography
} from "@mui/material";

// prodapi rejects OPTIONS preflight for these JSON endpoints, so keep them simple CORS requests.
const simpleJsonHeaders = { "Content-Type": "text/plain" };

const exportColumns = [
    { field: "id", header: "ID" },
    { field: "manufacturer", header: "Manufacturer" },
    { field: "bullet", header: "Bullet" },
    { field: "caliber", header: "Caliber" },
    { field: "weight", header: "Weight" },
    { field: "length", header: "Length" },
    { field: "bc_g1", header: "BC G1" },
    { field: "bc_gz", header: "BC GZ" },
    { field: "created_at", header: "Created" },
    { field: "updated_at", header: "Updated" },
];

const csvEscape = (value) => {
    if (value === null || value === undefined) return "";
    const stringValue = String(value);
    if (/[",\r\n]/.test(stringValue)) {
        return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
};

const getExportDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const downloadCsv = (filename, csvContent) => {
    const blob = new Blob(["\uFEFF", csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

export default function BulletLibrary( { theme: horusTheme } ) {

    /**
     * Data and selection states
     */
    const [rows, setRows] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [rowSelectionModel, setRowSelectionModel] = useState([]);

    /**
     * Dialog states for adding a bullet, large-delete warning, and general confirmation
     */
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [newBullet, setNewBullet] = useState({
        manufacturer: "",
        bullet: "",
        caliber: "",
        weight: "",
        length: "",
        bc_g1: "",
        bc_gz: "",
    });
    const [showLargeDeleteWarning, setShowLargeDeleteWarning] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);

    /**
     * Deletion progress tracking
     */
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteProgress, setDeleteProgress] = useState(0);
    const [deleteStatus, setDeleteStatus] = useState("");

    /**
     * Info dialog for displaying success/error messages in place of alerts
     */
    const [infoDialogOpen, setInfoDialogOpen] = useState(false);
    const [infoDialogTitle, setInfoDialogTitle] = useState("");
    const [infoDialogMessage, setInfoDialogMessage] = useState("");

    /**
     * Utility method to show the informational dialog with custom title and message
     */
    const showInfoDialog = (title, message) => {
        setInfoDialogTitle(title);
        setInfoDialogMessage(message);
        setInfoDialogOpen(true);
    };
    const handleInfoDialogClose = () => {
        setInfoDialogOpen(false);
        setInfoDialogTitle("");
        setInfoDialogMessage("");
    };

    /**
     * Fetches bullet data on component mount
     */
    useEffect(() => {
        // Pull api url from environment variable
        fetch(process.env.REACT_APP_API_URL + "/list-bullet")
            .then((res) => res.json())
            .then((data) => {
                if (data.status && Array.isArray(data.data)) {
                    const parsed = data.data.map((b) => ({
                        ...b,
                        id: parseInt(b.id, 10),
                    }));
                    setRows(parsed);
                } else {
                    showInfoDialog("Error", `Failed to list bullets: ${data.message}`);
                }
            })
            .catch((err) => {
                showInfoDialog("Error", `Could not list bullets. Check console for details.`);
                console.error("Error fetching bullets:", err);
            });
    }, []);

    /**
     * Refreshes bullet data, typically called after additions or deletions
     */
    const refreshData = () => {
        fetch(process.env.REACT_APP_API_URL + "/list-bullet")
            .then((res) => res.json())
            .then((data) => {
                if (data.status && Array.isArray(data.data)) {
                    const parsed = data.data.map((b) => ({
                        ...b,
                        id: parseInt(b.id, 10),
                    }));
                    setRows(parsed);
                    setRowSelectionModel([]);
                } else {
                    showInfoDialog("Error", `Failed to refresh bullets: ${data.message}`);
                }
            })
            .catch((err) => {
                showInfoDialog("Error", "Could not refresh bullets. Check console for details.");
                console.error("Error refreshing bullets:", err);
            });
    };

    /**
     * Filters the displayed rows based on the user's search query
     */
    const filteredRows = useMemo(() => {
        if (!searchText) return rows;
        const lower = searchText.toLowerCase();
        return rows.filter((row) =>
            String(row.manufacturer).toLowerCase().includes(lower) ||
            String(row.bullet).toLowerCase().includes(lower) ||
            String(row.caliber).toLowerCase().includes(lower) ||
            String(row.weight).toLowerCase().includes(lower) ||
            String(row.length).toLowerCase().includes(lower) ||
            String(row.bc_g1).toLowerCase().includes(lower) ||
            String(row.bc_gz).toLowerCase().includes(lower)
        );
    }, [rows, searchText]);

    const handleExportSpreadsheet = () => {
        if (rows.length === 0) {
            showInfoDialog("No Data", "There are no bullets to export.");
            return;
        }

        const headerRow = exportColumns.map((column) => csvEscape(column.header)).join(",");
        const dataRows = rows.map((row) =>
            exportColumns.map((column) => csvEscape(row[column.field])).join(",")
        );
        const csvContent = [headerRow, ...dataRows].join("\r\n");
        downloadCsv(`bullet-library-${getExportDate()}.csv`, csvContent);
    };

    /**
     * Updates a bullet row inline; triggered by the DataGrid's editing process
     */
    const processRowUpdate = async (newRow) => {
        const payload = {
            id: String(newRow.id),
            manufacturer: newRow.manufacturer,
            bullet: newRow.bullet,
            caliber: newRow.caliber,
            weight: newRow.weight,
            length: newRow.length,
            bc_g1: newRow.bc_g1,
            bc_gz: newRow.bc_gz,
        };
        try {
            const response = await fetch(process.env.REACT_APP_API_URL + "/update-bullet", {
                method: "POST",
                headers: simpleJsonHeaders,
                body: JSON.stringify(payload),
            });
            const data = await response.json();
            if (!data.status) {
                throw new Error(data.message || "Error updating bullet");
            }
            return newRow;
        } catch (err) {
            console.error("Update error:", err);
            showInfoDialog("Update Error", err.message || "Error updating bullet");
            throw err; // Let DataGrid revert this edit
        }
    };
    const handleRowUpdateError = () => {
        // Additional handling could be placed here if needed;
        // the info dialog is already triggered in processRowUpdate.
    };

    /**
     * Handles user's click on "Delete Selected" to check item count and show appropriate dialog
     */
    const handleBulkDeleteClick = () => {
        if (rowSelectionModel.length === 0) {
            showInfoDialog("No Selection", "No bullets selected for deletion.");
            return;
        }
        if (rowSelectionModel.length > 50) {
            setShowLargeDeleteWarning(true);
            return;
        }
        setShowConfirmDelete(true);
    };

    /**
     * Confirms or cancels a normal (<=50 items) deletion
     */
    const handleConfirmDeleteYes = () => {
        setShowConfirmDelete(false);
        doBulkDelete();
    };
    const handleConfirmDeleteNo = () => {
        setShowConfirmDelete(false);
    };

    /**
     * Confirms or cancels a large (>50 items) deletion
     */
    const handleConfirmLargeDelete = () => {
        setShowLargeDeleteWarning(false);
        doBulkDelete();
    };
    const handleCancelLargeDelete = () => {
        setShowLargeDeleteWarning(false);
    };

    /**
     * Performs the bulk delete in small batches (chunked concurrency).
     * Shows progress with a LinearProgress bar and status text.
     */
    const doBulkDelete = async () => {
        const count = rowSelectionModel.length;
        setIsDeleting(true);
        setDeleteProgress(0);
        setDeleteStatus(`Starting deletion of ${count} bullets...`);

        const bulletIds = [...rowSelectionModel];
        const total = bulletIds.length;
        let completed = 0;
        const concurrency = 5;

        const deleteOne = async (id) => {
            const res = await fetch(process.env.REACT_APP_API_URL + "/delete-bullet", {
                method: "POST",
                headers: simpleJsonHeaders,
                body: JSON.stringify({ id }),
            });
            const data = await res.json();
            if (!data.status) {
                throw new Error(data.message || `Error deleting bullet #${id}`);
            }
        };
        const deleteAndTrack = async (id) => {
            await deleteOne(id);
            completed++;
            const percent = Math.round((completed / total) * 100);
            setDeleteProgress(percent);
            setDeleteStatus(`Deleting bullet ${completed} of ${total}...`);
        };

        try {
            for (let i = 0; i < total; i += concurrency) {
                const chunk = bulletIds.slice(i, i + concurrency);
                await Promise.all(chunk.map(deleteAndTrack));
            }
            setIsDeleting(false);
            setDeleteProgress(0);
            setDeleteStatus("");
            showInfoDialog("Success", "Selected bullets deleted successfully!");
            refreshData();
        } catch (err) {
            console.error("Bulk delete error:", err);
            setIsDeleting(false);
            setDeleteProgress(0);
            setDeleteStatus("");
            showInfoDialog("Delete Error", err.message || "Error during bulk delete. Check console.");
        }
    };

    /**
     * Opens or closes the "Add Bullet" dialog and handles input changes
     */
    const handleOpenAddDialog = () => {
        setNewBullet({
            manufacturer: "",
            bullet: "",
            caliber: "",
            weight: "",
            length: "",
            bc_g1: "",
            bc_gz: "",
        });
        setOpenAddDialog(true);
    };
    const handleCloseAddDialog = () => setOpenAddDialog(false);

    const handleChangeNewBullet = (field, value) => {
        setNewBullet((prev) => ({ ...prev, [field]: value }));
    };

    /**
     * Submits the new bullet form data to the add-bullet endpoint
     * (multipart/form-data with a JSON file).
     */
    const handleAddBullet = async () => {
    try {
        /* 1️⃣  Wrap the single-bullet object in an array, then stringify */
        const bulletArray = [newBullet];               // [{ …fields… }]
        const jsonString  = JSON.stringify(bulletArray);

        /* 2️⃣  Turn the JSON into a Blob (or File) */
        const jsonBlob = new Blob([jsonString], {
        type: "application/json",
        });

        /* 3️⃣  Build FormData and append under field name "file" */
        const formData = new FormData();
        formData.append("file", jsonBlob, "bullet.json"); // <-- .json extension matters

        /* 4️⃣  POST -- DO NOT set Content-Type header manually */
        const res  = await fetch(
        process.env.REACT_APP_API_URL + "/add-bullet",
        { method: "POST", body: formData }
        );
        const data = await res.json();

        if (!data.status) {
        showInfoDialog("Add Error", data.message || "Error adding bullet");
        return;
        }
        showInfoDialog("Bullet Added", "Bullet added successfully!");
        handleCloseAddDialog();
        refreshData();
    } catch (err) {
        console.error("Error adding bullet:", err);
        showInfoDialog("Add Error", "Error adding bullet. Check console for details.");
    }
    };

    
    const { logout } = useAuth();
    const dashboard = () => {
        window.location.href = "/dashboard";
    }

    /**
     * Defines the DataGrid columns with inline editing enabled on relevant fields
     */
    const columns = [
        { field: "manufacturer", headerName: "Manufacturer", flex: 1, editable: true },
        { field: "bullet", headerName: "Bullet", flex: 1, editable: true },
        { field: "caliber", headerName: "Caliber", flex: 0.6, editable: true },
        { field: "weight", headerName: "Weight", flex: 0.6, editable: true },
        { field: "length", headerName: "Length", flex: 0.6, editable: true },
        { field: "bc_g1", headerName: "BC G1", flex: 0.6, editable: true },
        { field: "bc_gz", headerName: "BC GZ", flex: 0.6, editable: true },
        { field: "created_at", headerName: "Created", flex: 1 },
        { field: "updated_at", headerName: "Updated", flex: 1 },
    ];

    return (
        <ThemeProvider theme={horusTheme}>
            {/* Header */}
            <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                borderBottom: "1px solid #333",
                backgroundColor: "#000",
            }}
            >
            <h1 style={{ margin: 0, color: "#ff6600", fontSize: "1.8rem" }}>
                Bullet Library
            </h1>
            <Box>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={dashboard}
                    sx={{
                    borderColor: "#ff6600",
                    color: "#ff6600",
                    '&:hover': {
                        backgroundColor: "#ff6600",
                        color: "#000",
                    },
                    margin: "0 8px",
                    }}
                >
                    Dashboard
                </Button>
                    
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={logout}
                    sx={{
                    borderColor: "#ff6600",
                    color: "#ff6600",
                    '&:hover': {
                        backgroundColor: "#ff6600",
                        color: "#000",
                    },
                    }}
                >
                    Logout
                </Button>
            </Box>
            </Box>
            <Box sx={{ height: 700, width: "95%", m: "0 auto", pt: 4 }}>

                {isDeleting && (
                    <Box sx={{ mb: 2 }}>
                        <LinearProgress variant="determinate" value={deleteProgress} />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            {deleteStatus} ({deleteProgress}%)
                        </Typography>
                    </Box>
                )}

                {!isDeleting && (
                    <Box sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
                        <TextField
                            label="Search bullets..."
                            variant="outlined"
                            size="small"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            sx={{ flex: "1 1 280px" }}
                        />
                        <Button variant="contained" onClick={handleOpenAddDialog}>
                            Add Bullet
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleExportSpreadsheet}
                            disabled={rows.length === 0}
                            sx={{
                                borderColor: "#ff6600",
                                color: "#ff6600",
                                '&:hover': {
                                    backgroundColor: "#ff6600",
                                    color: "#000",
                                    borderColor: "#ff6600",
                                },
                            }}
                        >
                            Export Spreadsheet
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            disabled={rowSelectionModel.length === 0}
                            onClick={handleBulkDeleteClick}
                        >
                            Delete Selected ({rowSelectionModel.length})
                        </Button>
                    </Box>
                )}

                <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    checkboxSelection
                    disableSelectionOnClick
                    pagination
                    pageSizeOptions={[25, 50, 100]}
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={handleRowUpdateError}
                    components={{ Toolbar: GridToolbar }}
                    rowSelectionModel={rowSelectionModel}
                    onRowSelectionModelChange={(newModel) => setRowSelectionModel(newModel)}
                />
            </Box>

            {/* Add Bullet Dialog */}
            <Dialog open={openAddDialog} onClose={handleCloseAddDialog}>
                <DialogTitle>Add New Bullet</DialogTitle>
                <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    <TextField
                        label="Manufacturer"
                        value={newBullet.manufacturer}
                        onChange={(e) => handleChangeNewBullet("manufacturer", e.target.value)}
                    />
                    <TextField
                        label="Bullet"
                        value={newBullet.bullet}
                        onChange={(e) => handleChangeNewBullet("bullet", e.target.value)}
                    />
                    <TextField
                        label="Caliber"
                        value={newBullet.caliber}
                        onChange={(e) => handleChangeNewBullet("caliber", e.target.value)}
                    />
                    <TextField
                        label="Weight"
                        value={newBullet.weight}
                        onChange={(e) => handleChangeNewBullet("weight", e.target.value)}
                    />
                    <TextField
                        label="Length"
                        value={newBullet.length}
                        onChange={(e) => handleChangeNewBullet("length", e.target.value)}
                    />
                    <TextField
                        label="BC G1"
                        value={newBullet.bc_g1}
                        onChange={(e) => handleChangeNewBullet("bc_g1", e.target.value)}
                    />
                    <TextField
                        label="BC GZ"
                        value={newBullet.bc_gz}
                        onChange={(e) => handleChangeNewBullet("bc_gz", e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddDialog}>Cancel</Button>
                    <Button onClick={handleAddBullet} variant="contained">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog (for <= 50 items) */}
            <Dialog open={showConfirmDelete} onClose={handleConfirmDeleteNo}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete {rowSelectionModel.length} bullets?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirmDeleteNo}>Cancel</Button>
                    <Button onClick={handleConfirmDeleteYes} variant="contained" color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Large Deletion Warning (for > 50 items) */}
            <Dialog open={showLargeDeleteWarning} onClose={handleCancelLargeDelete}>
                <DialogTitle>Large Deletion Warning</DialogTitle>
                <DialogContent>
                    <Typography>
                        You have selected {rowSelectionModel.length} bullets to delete.
                        This is more than 50 items. Are you sure you want to proceed?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelLargeDelete}>Cancel</Button>
                    <Button onClick={handleConfirmLargeDelete} variant="contained" color="error">
                        Proceed
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Info Dialog for general success/error messages */}
            <Dialog open={infoDialogOpen} onClose={handleInfoDialogClose}>
                <DialogTitle>{infoDialogTitle}</DialogTitle>
                <DialogContent>
                    <Typography>{infoDialogMessage}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleInfoDialogClose} variant="contained">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
}
